import React, { useEffect, useState } from 'react';
import { TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Top 3 Movers - members who gained the most points in the last 7 days.
 * Mundalo Pacific feature panel (dark on purpose for visual contrast against
 * the surrounding light leaderboard surface).
 *
 *   • Background: Pacific      #1A5C6E
 *   • Sub-card:   Pacific-darker rgba(white,0.06)
 *   • Avatar:     Teal #2CA8A0 bg, navy initial
 *   • Name:       white
 *   • Points:     Golden Hour  #E8A830
 *   • Rank delta: up = #2CA8A0  / down = #F87171  / no-change = white/60
 */
const WeeklyMovers = ({ variant = 'full' }) => {
  const [movers, setMovers] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${API_URL}/api/community/leaderboard/movers?limit=3`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!r.ok) return;
        const d = await r.json();
        setMovers(d.movers || []);
      } catch {
        // silent - strip just hides
      }
    };
    run();
  }, []);

  if (!movers || movers.length === 0) return null;

  const RankDelta = ({ change }) => {
    if (change > 0) {
      return (
        <span className="inline-flex items-center text-[10px] text-[#2CA8A0]">
          <ArrowUp className="w-3 h-3" />
          {change}
        </span>
      );
    }
    if (change < 0) {
      return (
        <span className="inline-flex items-center text-[10px] text-[#F87171]">
          <ArrowDown className="w-3 h-3" />
          {Math.abs(change)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center text-[10px] text-white/60">
        <Minus className="w-3 h-3" />
      </span>
    );
  };

  const Avatar = ({ m, size }) => {
    const initial = (m.first_name || '?')[0]?.toUpperCase();
    if (m.profile_photo_url) {
      return (
        <img
          src={m.profile_photo_url}
          alt=""
          className="rounded-full object-cover shrink-0"
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <div
        className="rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center shrink-0"
        style={{ width: size, height: size, fontSize: size / 2.6 }}
      >
        {initial}
      </div>
    );
  };

  if (variant === 'compact') {
    return (
      <div
        className="p-3 rounded-lg bg-[#1A5C6E]"
        data-testid="weekly-movers-compact"
      >
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp className="w-3.5 h-3.5 text-[#2CA8A0]" />
          <span className="text-[11px] tracking-wider uppercase text-white/70 font-mono">
            Top movers · 7d
          </span>
        </div>
        <ul className="space-y-1.5">
          {movers.map((m, i) => (
            <li
              key={m.user_id}
              className="flex items-center gap-2 text-xs"
              data-testid={`mover-${i + 1}`}
            >
              <Avatar m={m} size={24} />
              <span className="flex-1 min-w-0 truncate text-white">
                {m.first_name}{m.last_name_initial ? ` ${m.last_name_initial}.` : ''}
              </span>
              <RankDelta change={m.rank_change} />
              <span className="text-[#E8A830] tabular-nums font-semibold">+{m.points_gained}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // full variant - Pacific panel, sits above the white leaderboard card.
  return (
    <div
      className="bg-[#1A5C6E] rounded-2xl p-5 mb-5 shadow-sm"
      data-testid="weekly-movers-full"
    >
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 text-[#2CA8A0]" />
        <h2 className="text-white text-sm font-medium tracking-wide">This week's top 3 movers</h2>
        <span className="text-white/55 text-xs">· last 7 days</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {movers.map((m, i) => (
          <div
            key={m.user_id}
            className="bg-white/8 backdrop-blur-sm rounded-lg p-3 flex items-center gap-3"
            style={{ background: 'rgba(255,255,255,0.07)' }}
            data-testid={`mover-${i + 1}`}
          >
            <Avatar m={m} size={36} />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm truncate font-medium">
                {m.first_name}{m.last_name_initial ? ` ${m.last_name_initial}.` : ''}
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[#E8A830] text-xs font-semibold tabular-nums">
                  +{m.points_gained} pts
                </span>
                <span className="text-white/40 text-xs">·</span>
                <RankDelta change={m.rank_change} />
                <span className="text-white/55 text-[11px] tabular-nums">
                  #{m.previous_rank} → #{m.current_rank}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeeklyMovers;
